'use client';

import Cookies from 'js-cookie';
import { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Box, Button, TextField, Alert, CircularProgress, Typography, Stack, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, MailOutline, LockOutlined } from '@mui/icons-material';
import Image from 'next/image';
import LoginLayout from '@/components/LoginLayout';
import { post } from '@/lib/fetch';

const clearAllCookies = () => {
  const allCookies = Cookies.get();
  Object.keys(allCookies).forEach((cookie) => Cookies.remove(cookie));
};

function SearchParamAlerts() {
  const searchParams = useSearchParams();
  const errorParam = searchParams.get('error');

  if (!errorParam) return null;

  let message = '';
  if (errorParam === 'invalid_invitation') {
    message = 'Invalid or expired invitation link';
  } else if (errorParam === 'missing_token') {
    message = 'Missing invitation token';
  }

  return (
    <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 1 }}>
      {message}
    </Alert>
  );
}

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      clearAllCookies();
      await post('/api/auth/login', { email: email.trim().toLowerCase(), password }, undefined, { redirectOnUnauthorized: false });

      const returnUrl = searchParams.get('returnUrl') || '/';
      router.push(returnUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.paper',
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'text.disabled' },
      '&.Mui-focused fieldset': { borderColor: 'text.primary', borderWidth: '1px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: 'text.primary' },
  } as const;

  return (
    <LoginLayout>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          backgroundColor: '#ffffff',
        }}
      >
        {/* Brand panel — mirrors the app's black AppBar */}
        <Box
          sx={{
            position: 'relative',
            backgroundColor: '#000000',
            color: '#ffffff',
            flex: { xs: '0 0 auto', md: '0 0 44%' },
            minHeight: { xs: 180, md: '100vh' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'center', md: 'space-between' },
            alignItems: { xs: 'center', md: 'flex-start' },
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Image src="/logo.png" alt="TLC" width={40} height={40} priority style={{ objectFit: 'contain' }} />
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: '0.18em',
                fontSize: '0.7rem',
              }}
            >
              TLC CRM
            </Typography>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 420 }}>
            <Typography
              variant="h3"
              sx={{
                color: '#ffffff',
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                mb: 2,
              }}
            >
              The Timeless Collector
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.6,
              }}
            >
              Inventory, orders, and operations
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: { xs: 'none', md: 'block' },
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.7rem',
              letterSpacing: '0.08em',
            }}
          ></Typography>
        </Box>

        {/* Form panel */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.default',
            px: { xs: 3, sm: 5 },
            py: { xs: 5, md: 6 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 380 }}>
            <Stack spacing={0.5} sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                Sign in
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Use your TLC credentials to continue.
              </Typography>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 1, fontSize: '0.875rem' }}>
                {error}
              </Alert>
            )}

            <Suspense fallback={null}>
              <SearchParamAlerts />
            </Suspense>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  size="medium"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutline sx={{ color: 'text.secondary', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <TextField
                  required
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  size="medium"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword((s) => !s)} edge="end" size="small" tabIndex={-1} sx={{ color: 'text.secondary' }}>
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  disabled={loading}
                  sx={{
                    mt: 1,
                    height: 44,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    backgroundColor: '#000',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#1a1a1a' },
                    '&.Mui-disabled': {
                      backgroundColor: '#000',
                      color: 'rgba(255,255,255,0.6)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
                </Button>
              </Stack>
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 4,
                color: 'text.secondary',
                fontSize: '0.7rem',
                letterSpacing: '0.04em',
              }}
            >
              © {new Date().getFullYear()} The Timeless Collector
            </Typography>
          </Box>
        </Box>
      </Box>
    </LoginLayout>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div></div>}>
      <LoginForm />
    </Suspense>
  );
}
